/**
 * Promise-based ADB shell runner.
 *
 * Wraps the fire-and-forget executeShell (which returns a channel id) into a
 * Promise that resolves with the accumulated stdout once the device sends CLSE,
 * or rejects on timeout.
 *
 * The OPEN/WRTE/CLSE correlation mirrors the reassembly in
 * useCuttlefishAdbOutput.tsx — subscribe before calling executeShell so fast
 * responses are never missed.
 *
 * Each emulator has its own in-browser ADB connection (one WebRTC data
 * channel), so callers are funneled through a small concurrency-limited
 * queue scoped to that emulator's `executeShell` callback. Without it, batch
 * reads like `getAllPropertyValues()` can fan out hundreds of simultaneous
 * `OPEN` streams on one connection, which saturates the transport and causes
 * cascading timeouts even for unrelated, otherwise-fast single-property
 * calls. Scoping the queue per `executeShell` (rather than one shared
 * module-level queue) keeps a read backlog on one emulator from delaying
 * writes to a different emulator open in the same session.
 */

import { getAdbMessageEmitter } from "./adbMessageEmitter";

// ADB protocol command constants — mirrors adb.ts / useCuttlefishAdbOutput.tsx.
const A_WRTE = 0x45545257;
const A_CLSE = 0x45534c43;

// Cap on simultaneous in-flight shell streams over a single shared ADB
// connection. Empirically the single WebRTC data channel + adbd's shell
// multiplexing can't keep up with the 100s of concurrent OPENs a
// full-catalog poll would otherwise issue; queuing keeps the channel
// responsive for everyone.
const MAX_CONCURRENT_SHELLS = 4;

// User-initiated writes (setProperty) are latency-sensitive and rare;
// background polling reads (getProperty/getAllPropertyValues) are frequent
// and best-effort. Without separating them, a write submitted while a
// full-catalog read is draining sits behind its entire FIFO backlog —
// observed taking 15-20s to reach the device — which reads back as a stuck
// UI control that reverts to the old value before "randomly" settling.
// Reserving one slot for "high" priority callers means a write only ever
// waits for a slot to free up, never for the whole read backlog to drain.
const RESERVED_FOR_HIGH = 1;

interface ShellQueue {
  activeShells: number;
  activeHighShells: number;
  highQueue: Array<() => void>;
  normalQueue: Array<() => void>;
}

function createQueue(): ShellQueue {
  return { activeShells: 0, activeHighShells: 0, highQueue: [], normalQueue: [] };
}

// Keyed by the `executeShell` function identity, which is unique per
// emulator connection (see useCuttlefishAdb). This isolates the concurrency
// cap per emulator instead of sharing one pool across every open emulator.
let queuesByExecuteShell = new WeakMap<
  (cmd: string) => number,
  ShellQueue
>();

function getQueue(executeShell: (cmd: string) => number): ShellQueue {
  let queue = queuesByExecuteShell.get(executeShell);
  if (!queue) {
    queue = createQueue();
    queuesByExecuteShell.set(executeShell, queue);
  }
  return queue;
}

/**
 * Test-only: clears all per-emulator queue state. Each test normally gets a
 * fresh `executeShell` closure (so a fresh queue), but this guards against
 * spurious cross-test state if a test ever reuses the same function
 * reference or leaves a queue dirty mid-assertion.
 */
export function resetShellQueue(): void {
  queuesByExecuteShell = new WeakMap();
}

function scheduleNext(queue: ShellQueue): void {
  if (queue.activeShells >= MAX_CONCURRENT_SHELLS) return;

  const next = queue.highQueue.shift();
  if (next) {
    queue.activeShells++;
    queue.activeHighShells++;
    next();
    scheduleNext(queue);
    return;
  }

  const normalCap = MAX_CONCURRENT_SHELLS - RESERVED_FOR_HIGH;
  if (queue.activeShells - queue.activeHighShells >= normalCap) return;
  const normalNext = queue.normalQueue.shift();
  if (!normalNext) return;
  queue.activeShells++;
  normalNext();
}

function getU32LE(buf: Uint8Array, offset: number): number {
  return (
    ((buf[offset] ?? 0) |
      ((buf[offset + 1] ?? 0) << 8) |
      ((buf[offset + 2] ?? 0) << 16) |
      ((buf[offset + 3] ?? 0) << 24)) >>>
    0
  );
}

function runShellImmediate(
  executeShell: (cmd: string) => number,
  command: string,
  timeoutMs: number,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const emitter = getAdbMessageEmitter();
    let output = "";
    let localId: number | undefined;

    const onMsg = (buf: ArrayBuffer): void => {
      const array = new Uint8Array(buf);
      if (array.length < 12) return; // too short to have arg0/arg1

      const cmd = getU32LE(array, 0);
      const arg1 = getU32LE(array, 8);

      if (cmd === A_WRTE && arg1 === localId) {
        // Payload starts at byte 24 (after the 24-byte ADB header).
        const payloadLen =
          array.length >= 16 ? getU32LE(array, 12) : array.length - 24;
        if (array.length > 24 && payloadLen > 0) {
          const payload = array.slice(24, 24 + payloadLen);
          output += new TextDecoder("utf-8", { fatal: false }).decode(payload);
        }
      } else if (cmd === A_CLSE && arg1 === localId) {
        cleanup();
        resolve(output.replace(/\0+$/g, "").trimEnd());
      }
    };

    const unsub = emitter.subscribe(onMsg);
    const t = setTimeout(() => {
      unsub();
      reject(new Error(`adb shell timeout (${timeoutMs} ms): ${command}`));
    }, timeoutMs);

    const cleanup = (): void => {
      unsub();
      clearTimeout(t);
    };

    // Subscribe BEFORE calling executeShell to avoid missing fast responses.
    localId = executeShell(command);
    if (localId < 0) {
      cleanup();
      reject(new Error("ADB not available"));
    }
  });
}

/**
 * Run a shell command over the in-browser ADB connection and return its stdout.
 *
 * Queues behind other in-flight calls of the same priority on the same
 * emulator so its shared ADB connection never gets flooded with
 * simultaneous streams. `"high"` priority (user-initiated writes) reserves
 * one of `MAX_CONCURRENT_SHELLS` slots so it never waits behind a large
 * backlog of `"normal"` priority background reads. The queue is scoped to
 * the `executeShell` callback passed in, so concurrent emulators don't
 * compete for the same slots.
 *
 * @param executeShell - from useCuttlefishAdb(); returns the channel id
 * @param command      - shell command string, e.g. "cmd car_service get-property-value 289408000 0"
 * @param timeoutMs    - reject if CLSE not received within this window (default 5 s)
 * @param priority     - "high" for user-initiated writes, "normal" (default) for background reads
 */
export function runShell(
  executeShell: (cmd: string) => number,
  command: string,
  timeoutMs = 5000,
  priority: "high" | "normal" = "normal",
): Promise<string> {
  return new Promise((resolve, reject) => {
    const queue = getQueue(executeShell);
    const isHigh = priority === "high";
    const start = (): void => {
      runShellImmediate(executeShell, command, timeoutMs).then(
        (out) => {
          queue.activeShells--;
          if (isHigh) queue.activeHighShells--;
          scheduleNext(queue);
          resolve(out);
        },
        (err: unknown) => {
          queue.activeShells--;
          if (isHigh) queue.activeHighShells--;
          scheduleNext(queue);
          reject(err as Error);
        },
      );
    };
    (isHigh ? queue.highQueue : queue.normalQueue).push(start);
    scheduleNext(queue);
  });
}
